import type { Action, Decision, Policy } from '@shared/core/policy';
import { useCallback, useMemo } from 'react';

import { useActor } from './PolicyProvider';

export type PolicyEvaluator<Facts, Field extends string, Op extends string = never> = (
  facts: Facts,
  action: Action<Field, Op>
) => Decision;

export function usePolicyEvaluator<Facts, Field extends string, Op extends string = never>(
  policy: Policy<Facts, Field, Op>
): PolicyEvaluator<Facts, Field, Op> {
  const actor = useActor();

  return useCallback((facts, action) => policy.decide(actor, facts, action), [actor, policy]);
}

export type PolicyHandle<Field extends string, Op extends string = never> = {
  /** Can the actor SEE it (the entity, or one field)? A denial means render NOTHING. */
  view: (field?: Field) => Decision;
  add: () => Decision;
  /** No field = entity-level: header forms, Edit buttons. With field = one column. */
  edit: (field?: Field) => Decision;
  /** `remove`, not `delete` — the latter is a reserved word. */
  remove: () => Decision;
  /** Named operations: can.perform('void'). */
  perform: (op: Op) => Decision;
  /** True when anything is editable. Feeds EditableDataGrid.canEdit and Edit buttons. */
  any: () => boolean;
  /** Surface-wide lock sentence for a banner, or null when nothing is locked. */
  lockMessage: () => string | null;
  /**
   * Spread onto a column definition:
   *
   *   { field: 'settlementAmount', ...can.column('settlementAmount') }
   *
   * Both halves come from ONE decide() call, so the greyed cell and its
   * tooltip can never disagree. Do not hand-write the pair.
   */
  column: (field: Field) => { isCellEditable: (row: unknown) => boolean; lockReason: (row: unknown) => string | null };
};

/**
 * Bind a shared policy to this render's actor and facts.
 *
 *   usePolicy(salesOrderPolicy, facts)  ──▶  { any, edit, perform, column, … }
 *
 * The actor comes from PolicyProvider; consumers never see it. That is what
 * makes Clerk a one-file change on the web side.
 */
export function usePolicy<Facts, Field extends string, Op extends string = never>(
  policy: Policy<Facts, Field, Op>,
  facts: Facts
): PolicyHandle<Field, Op> {
  const actor = useActor();

  return useMemo(() => {
    const editDecision = (field?: Field) =>
      policy.decide(actor, facts, field === undefined ? { type: 'edit' } : { type: 'edit', field });

    const anyEditable = () =>
      policy.fields.length > 0 ? policy.fields.some((field) => editDecision(field).allowed) : editDecision().allowed;

    const firstDenial = (): Decision | null => {
      if (policy.fields.length === 0) {
        const decision = editDecision();
        return decision.allowed ? null : decision;
      }
      for (const field of policy.fields) {
        const decision = editDecision(field);
        if (!decision.allowed) return decision;
      }
      return null;
    };

    return {
      view: (field?: Field) =>
        policy.decide(actor, facts, field === undefined ? { type: 'view' } : { type: 'view', field }),
      add: () => policy.decide(actor, facts, { type: 'add' }),
      edit: editDecision,
      remove: () => policy.decide(actor, facts, { type: 'delete' }),
      perform: (op: Op) => policy.decide(actor, facts, { type: 'perform', op }),
      any: anyEditable,
      lockMessage: () => {
        // A surface-wide banner is only honest when EVERYTHING is locked;
        // a single locked column belongs in that cell's tooltip, not a banner.
        if (anyEditable()) return null;
        const denial = firstDenial();
        return denial && !denial.allowed ? denial.message : null;
      },
      column: (field: Field) => {
        const decision = editDecision(field);
        return {
          isCellEditable: () => decision.allowed,
          lockReason: () => (decision.allowed ? null : decision.message),
        };
      },
    };
  }, [actor, policy, facts]);
}
